<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { onMounted, toValue } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import SpinningRingIcon from '../Components/Icons/SpinningRingIcon.vue'
import PageTitle from '../Components/PageTitle.vue'
import PrivacyStatement from '../Components/PrivacyStatement.vue'
import QuestionFormField from '../Components/Questions/QuestionFormField.vue'
import SubmitButton from '../Components/SubmitButton.vue'
import {
    CannotCreateEligibilitySubmission,
    CannotGetQuestions,
    PostalCodeMissing,
    StateAlreadyFilled,
} from '../Errors'
import { Action, Actor, enrollmentTracking } from '../Integrations/Tracking'
import BaseLayout from '../Layouts/BaseLayout.vue'
import { useConfigStore } from '../Stores/config'
import { useEligibilitySubmissionStore } from '../Stores/eligibilitySubmission'
import { useQuestionStore } from '../Stores/question'
import { useEnrollment, useValidation } from '../composables'
import { useInteractionTracking } from '../composables/useInteractionTracking'
import { Locale } from '../global'
import { PostmessageExternalSender } from '../postmessage-api-external'
import { marketingSourceFromQuery, toAtomString } from '../utils'

const { locale } = useI18n()
const language: Locale = toValue(locale) as Locale

const route = useRoute()
const router = useRouter()

const configStore = useConfigStore()
const eligibilitySubmissionStore = useEligibilitySubmissionStore()
const questionStore = useQuestionStore()

const { loading, eligibilitySubmission } = storeToRefs(eligibilitySubmissionStore)
const { generalQuestions } = storeToRefs(questionStore)

const { invalidFields, fails, validate } = useValidation()
const enrollment = useEnrollment()
const { trackSelectOption, trackClickButton } = useInteractionTracking()

onMounted((): void => {
    const postMessageExternalSender = new PostmessageExternalSender()

    postMessageExternalSender.pageReady()
    postMessageExternalSender.getDeviceSettingsStatus({ pushMessage: true })
})

const next = async () => {
    validate(generalQuestions.value.questions)

    trackClickButton({ all_fields_filled: !fails.value })

    if (fails.value) {
        return
    }

    if (!eligibilitySubmissionStore.campaignSwapPIScreen)
        enrollmentTracking.track(Actor.Homeowner, Action.StartSubmission)

    if (!questionStore.generalQuestionsArePrefilled) {
        await eligibilitySubmissionStore.answerQuestions(generalQuestions.value.questions)
    }

    await eligibilitySubmissionStore.getAssessment()

    if (!eligibilitySubmission.value.assessment?.qualified) {
        enrollmentTracking.track(Actor.Homeowner, Action.IneligibleBecauseUnqualified)

        return router.push({ name: 'unqualified' })
    }

    if (
        !eligibilitySubmissionStore.campaignSwapPIScreen &&
        (eligibilitySubmissionStore.isMissingPersonalDetails ||
            configStore.shouldDisplayPersonalDetails)
    ) {
        configStore.$patch({ shouldDisplayPersonalDetails: true })
        return router.push({ name: 'personal-details', query: route.query })
    }

    if (
        !eligibilitySubmissionStore.campaignSwapPIScreen &&
        (eligibilitySubmissionStore.isMissingBusinessDetails ||
            configStore.shouldDisplayBusinessDetails)
    ) {
        configStore.$patch({ shouldDisplayBusinessDetails: true })
        return router.push({ name: 'business-details', query: route.query })
    }

    if (!configStore.isKbcOrEnergyAtHomePartner) {
        eligibilitySubmissionStore.touchPrivacyPolicyDisplayedAt()
    }

    return enrollment.start()
}

try {
    if (eligibilitySubmissionStore.hasStateFilled) {
        throw new StateAlreadyFilled('State already filled')
    }

    if (route.query.campaignId && route.query.ssoTokenKBC) {
        // check if there are any entries for the campaign and KBC user
        await eligibilitySubmissionStore.check(
            route.query.campaignId as string,
            route.query.ssoTokenKBC as string
        )
    } else if (route.query.entryToken) {
        await eligibilitySubmissionStore.createFromEntry(
            route.query.entryToken as string,
            route.query.campaignId as string
        )
    } else if (route.query.submissionToken && route.query.submissionId) {
        await eligibilitySubmissionStore.createFromSubmission(
            route.query.submissionToken as string,
            route.query.submissionId as string
        )
    } else {
        await eligibilitySubmissionStore.createFromCampaign(route.query.campaignId as string, {
            externalId: route.query.externalId?.toString(),
            firstName: route.query.firstName?.toString(),
            lastName: route.query.lastName?.toString(),
            companyName: route.query.companyName?.toString(),
            vatNumber: route.query.vatNumber?.toString(),
            emailAddress: encodeURIComponent(route.query.emailAddress?.toString() ?? '')
                .replace(/%20/g, '+')
                .replace(/%40/g, '@'),
            phoneNumber: route.query.phoneNumber?.toString(),
            street: route.query['address[street]']?.toString(),
            number: route.query['address[number]']?.toString(),
            extension: route.query['address[extension]']?.toString(),
            city: route.query['address[city]']?.toString(),
            postalCode: route.query['address[postalCode]']?.toString(),
            privacyPolicyDisplayedAt: toAtomString(new Date()),
            language,
            ...marketingSourceFromQuery(route.query),
        })
    }

    if (eligibilitySubmissionStore.error) {
        throw new CannotCreateEligibilitySubmission('Error creating eligibility submission')
    }

    if (
        eligibilitySubmissionStore.campaignSwapPIScreen &&
        !eligibilitySubmissionStore.eligibilitySubmission.address?.postalCode
    ) {
        throw new PostalCodeMissing('Postal code missing')
    } else {
        await questionStore.getQuestions(route.query.campaignId as string)

        if (questionStore.error) {
            throw new CannotGetQuestions('Error getting questions')
        }
    }
} catch (error) {
    if (error instanceof CannotCreateEligibilitySubmission || error instanceof CannotGetQuestions) {
        router.push({ name: 'error' })
    } else if (error instanceof PostalCodeMissing) {
        configStore.$patch({ shouldDisplayPostalCode: true })
        router.push({ name: 'postal-code', query: route.query, replace: true })
    } else if (error instanceof StateAlreadyFilled && questionStore.generalQuestionsArePrefilled) {
        await next()
    }
}
</script>

<template>
    <BaseLayout>
        <div class="flex-1">
            <PageTitle>{{ generalQuestions.name }}</PageTitle>
            <div class="mt-6 space-y-8">
                <QuestionFormField
                    v-for="question in generalQuestions.questions"
                    :question="question"
                    :key="question.questionId"
                    v-model="question.answer"
                    :errorMessage="invalidFields.get(question.questionId)"
                    @select="(answerKey) => trackSelectOption(question.key, answerKey)"
                />
            </div>
        </div>
        <template #footer>
            <PrivacyStatement v-if="!loading" />
            <SubmitButton @click="next" :disabled="loading">
                <SpinningRingIcon v-if="loading" />
                <template v-else>{{ $t('Next') }}</template>
            </SubmitButton>
        </template>
    </BaseLayout>
</template>
